import { ChangeEvent, useEffect, useState } from 'react'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import { uploadImages } from '@/core/services/upload-image'
import {
  PartnerDto,
  RewardConfigDto,
  RewardDefinitionDto,
  RewardDefinitionTranslationDto,
  RewardDefinitionUpsertDto,
  RewardPoolDto,
  emptyRewardConfig,
} from '@/common/models/gamification'

import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { RewardConfigFields } from './RewardConfigFields'
import { TranslationTabs } from './shared/TranslationTabs'

interface CreateRewardDefinitionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  definition?: RewardDefinitionDto | null
  partners: PartnerDto[]
  pools: RewardPoolDto[]
  onSubmit: (values: RewardDefinitionUpsertDto, id?: string) => void
  onDelete?: (id: string) => void
}

/** True when the config's required external-id field is non-blank (PHYSICAL has none). */
const isConfigValid = (config: RewardConfigDto): boolean => {
  switch (config.typeKey) {
    case 'COIN_VOUCHER':
      return config.coinVoucherId.trim() !== ''
    case 'DISCOUNT_VOUCHER':
      return config.voucherId.trim() !== ''
    case 'TICKET':
    case 'HOTEL':
      return config.ticketId.trim() !== ''
    case 'PHYSICAL':
      return true
  }
}

export const CreateRewardDefinitionModal = ({
  open,
  onOpenChange,
  definition,
  partners,
  pools,
  onSubmit,
  onDelete,
}: CreateRewardDefinitionModalProps) => {
  const isEditMode = !!definition
  const [deleteOpen, setDeleteOpen] = useState(false)

  const [config, setConfig] = useState<RewardConfigDto>(
    emptyRewardConfig('COIN_VOUCHER'),
  )
  const [translations, setTranslations] = useState<
    RewardDefinitionTranslationDto[]
  >([])
  const [partnerId, setPartnerId] = useState('')
  const [pointPrice, setPointPrice] = useState('')
  const [displayOnly, setDisplayOnly] = useState(false)
  const [rewardPoolId, setRewardPoolId] = useState('')
  const [photoRef, setPhotoRef] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | undefined>()

  useEffect(() => {
    if (!open) return
    if (definition) {
      setConfig(definition.config)
      setTranslations(definition.translations ?? [])
      setPartnerId(definition.partnerId ?? '')
      setPointPrice(
        definition.pointPrice != null ? String(definition.pointPrice) : '',
      )
      setDisplayOnly(definition.displayOnly)
      setRewardPoolId(definition.rewardPoolId ?? '')
      setPhotoRef(definition.photoRef ?? '')
    } else {
      setConfig(emptyRewardConfig('COIN_VOUCHER'))
      setTranslations([])
      setPartnerId('')
      setPointPrice('')
      setDisplayOnly(false)
      setRewardPoolId('')
      setPhotoRef('')
    }
    setUploadError(undefined)
    setIsUploading(false)
  }, [open, definition])

  const handlePhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1_000_000) {
      setUploadError('The photo is too large (max 1 MB)')
      return
    }
    setUploadError(undefined)
    setIsUploading(true)
    try {
      const { data } = await uploadImages(file, true, 'GAMIFICATION_REWARD')
      if (data?.photoUrl) setPhotoRef(data.photoUrl)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setUploadError(
        status === 400
          ? 'Image must be at least 600px wide'
          : 'Photo upload failed',
      )
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => onOpenChange(false)

  const configValid = isConfigValid(config)
  const pointPriceValid = pointPrice === '' || Number(pointPrice) >= 0

  const partnerOptions = [
    { value: 'none', label: 'None' },
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ]

  const poolOptions = [
    { value: 'none', label: 'None' },
    ...pools.map((p) => ({
      value: p.id,
      label: partners.find((partner) => partner.id === p.partnerId)?.name ?? p.id,
    })),
  ]

  const submit = () => {
    if (!configValid || !pointPriceValid) return
    onSubmit(
      {
        config,
        // A display-only reward is never claimable, so it must not carry a price — the API rejects
        // the pair. The price input is disabled and cleared above, this is the belt-and-braces.
        pointPrice:
          displayOnly || pointPrice === '' ? undefined : Number(pointPrice),
        displayOnly,
        partnerId: partnerId || undefined,
        rewardPoolId: rewardPoolId || undefined,
        photoRef: photoRef || undefined,
        translations,
      },
      definition?.id,
    )
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="gift"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Reward</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode && onDelete ? (
              <Button
                onClick={() => setDeleteOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                onClick={submit}
                type="button"
                variant="primary"
                disabled={!configValid || !pointPriceValid}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          <RewardConfigFields value={config} onChange={setConfig} />

          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={displayOnly}
              onCheckedChange={(checked) => {
                const next = !!checked
                setDisplayOnly(next)
                // A display-only reward can never be bought, so a price would contradict it.
                if (next) setPointPrice('')
              }}
            />
            <span>
              <span className="text-body-medium text-monochrome-1">
                Display only
              </span>
              <span className="block text-xs text-monochrome-2">
                Shown in the app but never given out — a grand prize or a sponsor
                item you hand over yourself. It cannot be bought with points or
                awarded by a rule.
              </span>
            </span>
          </label>

          <FormGroup
            label="Point price"
            error={!pointPriceValid ? 'Must be greater than or equal to 0' : undefined}
          >
            <Input
              isInvalid={!pointPriceValid}
              value={pointPrice}
              onChange={(e) => setPointPrice(e.target.value)}
              type="number"
              min="0"
              disabled={displayOnly}
              placeholder={
                displayOnly ? 'Not available for display-only rewards' : 'Enter point price'
              }
            />
          </FormGroup>

          <SelectField
            label="Partner"
            placeholder="None"
            options={partnerOptions}
            value={partnerId || 'none'}
            onValueChange={(v) => setPartnerId(v === 'none' ? '' : v)}
          />

          <SelectField
            label="Reward pool"
            placeholder="None"
            options={poolOptions}
            value={rewardPoolId || 'none'}
            onValueChange={(v) => setRewardPoolId(v === 'none' ? '' : v)}
          />

          <FormUploadPreview
            label="Photo"
            previewLabel="Photo"
            isUploading={isUploading}
            url={photoRef}
            imageName={photoRef ? 'reward photo' : undefined}
            onRemove={() => setPhotoRef('')}
            imageStyle={{ objectFit: 'contain' }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={!!uploadError}
                type="file"
                className="bg-monochrome-5-50"
                onChange={handlePhotoUpload}
              />
            }
          />
          {uploadError && (
            <span className="text-xs text-accent-3">{uploadError}</span>
          )}

          <TranslationTabs<RewardDefinitionTranslationDto>
            value={translations}
            onChange={setTranslations}
            makeEmpty={(locale) => ({ locale, title: '', description: '' })}
            renderFields={({ value, onFieldChange }) => (
              <div className="space-y-4">
                <FormGroup label="Title">
                  <Input
                    value={value.title ?? ''}
                    onChange={(e) => onFieldChange({ title: e.target.value })}
                    type="text"
                    placeholder="Enter title"
                    maxLength={256}
                  />
                </FormGroup>
                <FormGroup label="Description">
                  <Input
                    value={value.description ?? ''}
                    onChange={(e) =>
                      onFieldChange({ description: e.target.value })
                    }
                    type="text"
                    placeholder="Enter description"
                    maxLength={512}
                  />
                </FormGroup>
              </div>
            )}
          />
        </div>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteOpen}
        setDeleteConfirmationModal={setDeleteOpen}
        deletedItem="reward"
        deleteFunc={() => {
          if (definition && onDelete) onDelete(definition.id)
          handleClose()
        }}
      />
    </>
  )
}
